import { create } from 'zustand';
import i18n from '@/utils/i18n';
import {
	isLanguage,
	persistLanguage,
	resolveInitialLanguage,
	type Language,
} from '@/utils/language';

interface LanguageState {
	/** Текущий язык интерфейса -- отражение языка i18next, а не отдельное мнение о нём */
	language: Language;
	/**
	 * Выбор пользователя: запоминает его и переключает словарь. Состояние
	 * стора и документ обновляет слушатель смены языка ниже.
	 */
	setLanguage: (language: Language) => void;
}

/** Проставляет язык там, где его читают браузер и программы экранного доступа */
function applyToDocument(language: Language): void {
	document.documentElement.lang = language;
}

/**
 * Язык, на котором сейчас работает i18next. resolvedLanguage всегда из
 * поддерживаемых (supportedLngs в i18n.ts); пустым он бывает только до init,
 * и на этот случай берётся тот же язык, с которым init вызывается.
 */
function activeLanguage(): Language {
	const { resolvedLanguage } = i18n;
	return isLanguage(resolvedLanguage) ? resolvedLanguage : resolveInitialLanguage();
}

export const useLanguageStore = create<LanguageState>(() => ({
	language: activeLanguage(),
	setLanguage: (language) => {
		persistLanguage(language);
		void i18n.changeLanguage(language);
	},
}));

/*
 * Язык меняет только i18next, стор за ним следует. Иначе вызов
 * changeLanguage в обход стора -- обвязка тестов, e2e -- оставил бы стор и
 * документ на прежнем языке: русский текст с английскими датами и числами.
 * В хранилище слушатель не пишет: там живёт только выбор пользователя, и
 * технический вызов перезаписывать его не должен. Отписка не нужна: стор
 * живёт всё время работы приложения.
 */
i18n.on('languageChanged', () => {
	const language = activeLanguage();
	applyToDocument(language);
	useLanguageStore.setState({ language });
});

/**
 * Проставляет язык документа при старте. Слушатель срабатывает только на
 * смену языка, и до неё атрибут `lang` оставался бы тем, что прописан в
 * `index.html`.
 */
export function applyInitialLanguage(): void {
	applyToDocument(useLanguageStore.getState().language);
}
